import {
  isIosSafariBrowserTab,
  isPwaListedAsInstalledRelatedApp,
  isRunningAsInstalledPwa,
} from './pwaInstallEnvironment';

export const PWA_INSTALLED_KEY = 'pwa-install-complete';
export const PWA_IOS_DISMISSED_SESSION_KEY = 'pwa-ios-install-dismissed';

/** Wait for display-mode to settle before treating beforeinstallprompt as "not installed". */
const INSTALL_PROMPT_SETTLE_MS = 400;

export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

type Listener = () => void;

let captureStarted = false;
let deferredPrompt: BeforeInstallPromptEvent | null = null;
let pendingPrompt: BeforeInstallPromptEvent | null = null;
let settleTimer: ReturnType<typeof setTimeout> | null = null;
const listeners = new Set<Listener>();

function notify(): void {
  listeners.forEach((listener) => listener());
}

function rememberInstalled(): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(PWA_INSTALLED_KEY, 'true');
}

function discardPendingOffer(): void {
  pendingPrompt = null;
  deferredPrompt = null;
  if (settleTimer !== null) {
    if (typeof window !== 'undefined') window.clearTimeout(settleTimer);
    settleTimer = null;
  }
}

/**
 * Drop a leftover Chromium install flag when we land in an iOS Safari tab.
 * Do not clear the flag merely because the document is not yet standalone —
 * desktop PWAs can report `display-mode: browser` for a tick on launch, and
 * a regular tab after install is still "already installed".
 */
export function clearStalePwaInstallFlag(): void {
  if (typeof window === 'undefined' || isRunningAsInstalledPwa()) return;
  if (isIosSafariBrowserTab()) {
    localStorage.removeItem(PWA_INSTALLED_KEY);
  }
}

async function finalizeCapturedInstallPrompt(): Promise<void> {
  const captured = pendingPrompt;
  pendingPrompt = null;
  if (!captured) return;

  if (isRunningAsInstalledPwa() || (await isPwaListedAsInstalledRelatedApp())) {
    deferredPrompt = null;
    rememberInstalled();
    notify();
    return;
  }

  // Browser still considers the app installable (first visit or after uninstall).
  deferredPrompt = captured;
  if (typeof window !== 'undefined') {
    localStorage.removeItem(PWA_INSTALLED_KEY);
  }
  notify();
}

function handleBeforeInstallPrompt(event: Event): void {
  event.preventDefault();
  pendingPrompt = event as BeforeInstallPromptEvent;
  deferredPrompt = null;

  if (isRunningAsInstalledPwa()) {
    discardPendingOffer();
    rememberInstalled();
    notify();
    return;
  }

  if (settleTimer !== null) window.clearTimeout(settleTimer);
  settleTimer = window.setTimeout(() => {
    settleTimer = null;
    void finalizeCapturedInstallPrompt();
  }, INSTALL_PROMPT_SETTLE_MS);
}

function handleAppInstalled(): void {
  discardPendingOffer();
  if (!isIosSafariBrowserTab()) {
    rememberInstalled();
  }
  notify();
}

/** Capture `beforeinstallprompt` once so Settings and the bottom banner share the same offer. */
export function ensurePwaInstallOfferCapture(): void {
  if (typeof window === 'undefined' || captureStarted) return;
  captureStarted = true;
  clearStalePwaInstallFlag();
  window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  window.addEventListener('appinstalled', handleAppInstalled);
}

export function subscribePwaInstallOffer(listener: Listener): () => void {
  ensurePwaInstallOfferCapture();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getDeferredInstallPrompt(): BeforeInstallPromptEvent | null {
  return deferredPrompt;
}

export function clearDeferredInstallPrompt(): void {
  if (!deferredPrompt && !pendingPrompt) return;
  discardPendingOffer();
  notify();
}

export function markPwaInstalled(): void {
  if (typeof window === 'undefined') return;
  rememberInstalled();
  discardPendingOffer();
  notify();
}
